import { useCallback, useState, type ReactElement } from 'react';
import { CornerDownRight } from 'lucide-react';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable } from '../../../../components/data-table/useDataTable';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { FolderTabs } from '../../../../components/FolderTabs';
import { CountBadge } from '../../../../components/CountBadge';
import { TextCell } from '../../../../components/table/TextCell';
import { Tooltip } from '../../../../components/Tooltip';
import { StatusText } from '../../../../components/table/StatusText';
import { formatDateOnly } from '../../../../lib/formatDate';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { leaveCreditApi, leaveRecordApi } from '../../leave/leaveApi';
import {
  LEAVE_BALANCE_DISCREPANCY_TEXT,
  LEAVE_ENTRY_STATUS_LABELS,
  LEAVE_ENTRY_TYPE_LABELS,
  LeaveEntryStatus,
  type LeaveCreditBalance,
  type LeaveLedgerEntry,
  type LeaveRecord,
} from '../../leave/leaveTypes';
import { LeaveLedgerEntryModal } from './LeaveLedgerEntryModal';
import { LeaveTypeLedgerModal } from './LeaveTypeLedgerModal';
import { LeaveRecordModal } from './LeaveRecordModal';
import { LeaveFigure as Figure } from '../../leave/components/LeaveFigure';
import { LeaveLedgerTypeRail, type LeaveLedgerRailType, type LeaveLedgerTypeFilter } from './LeaveLedgerTypeRail';

type Folder = 'balance' | 'ledger' | 'history';

/** A ledger row written by a Leave History record is changed there; the server refuses it here too. */
const RECORD_OWNED_REASON = 'This entry was written by a Leave History record. Edit, delete or restore it there.';

/** Who created a ledger row the system wrote from a Leave History record — the user's words. */
const SYSTEM_GENERATED = 'System generated';

/**
 * A Credit Balance cell on a row with a POSSIBLE discrepancy — the row is tinted red, and
 * hovering (or focusing) the Type or Balance cell says why, in the sentences the rows prove.
 * `block`: a table cell is a full-width context. Anything else renders untouched.
 */
function Flagged({ row, children }: { row: LeaveCreditBalance; children: ReactElement }) {
  const flags = row.discrepancies ?? [];
  if (flags.length === 0) return children;

  return (
    <Tooltip
      block
      label={
        <span className="flex flex-col gap-1">
          <span className="font-medium">Possible discrepancy</span>
          {flags.map((flag) => (
            <span key={flag}>{LEAVE_BALANCE_DISCREPANCY_TEXT[flag]}</span>
          ))}
        </span>
      }
    >
      <span className="block min-w-0">{children}</span>
    </Tooltip>
  );
}

/**
 * One employee's leave credits — the balance per type, and the ledger behind it.
 *
 * **Not a section of the 201 file.** It is a running account the organization keeps ABOUT this
 * employee, like the custody register Logistics keeps, which is why it sits outside the
 * `201 File` group beside Issued Assets and Account, and why it carries `leave-credits.*`
 * rather than a personnel key.
 *
 * **Credit Balance and Leave Ledger read the SAME rows.** Credit Balance is an aggregate of the
 * ledger and Leave Ledger is the ledger itself, so the two can never disagree about what the
 * employee has — there is no second place a balance is kept. **Leave History** is the leave
 * taken; a record that deducts does so by owning one Used row in that same ledger.
 */
export function LeaveCreditsPanel({
  personnelId,
  employeeUserId,
}: {
  personnelId: string;
  /** The employee's own linked account, if any — what a new leave record's Filed by opens on. */
  employeeUserId: string | null;
}) {
  const toast = useToast();
  const [folder, setFolder] = useState<Folder>('balance');
  const [creating, setCreating] = useState(false);
  // The balance row whose ledger is open, from its Balance figure.
  const [viewing, setViewing] = useState<LeaveCreditBalance | null>(null);
  const [editing, setEditing] = useState<LeaveLedgerEntry | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  // The ledger's rail: which type it is narrowed to, and every type with its count.
  const [typeFilter, setTypeFilter] = useState<LeaveLedgerTypeFilter>('all');
  const [railTypes, setRailTypes] = useState<LeaveLedgerRailType[]>([]);
  const [ledgerAll, setLedgerAll] = useState<number | undefined>(undefined);

  const [recordCreating, setRecordCreating] = useState(false);
  const [recordEditing, setRecordEditing] = useState<LeaveRecord | null>(null);
  const [recordDeleting, setRecordDeleting] = useState<LeaveRecord[] | null>(null);
  const [showDeletedRecords, setShowDeletedRecords] = useState(false);

  /*
    The whole list arrives and is searched, sorted and paged IN MEMORY — the exemption a 201
    file's sections already take. Eleven rows is not a page, and the figures are an aggregate
    of the ledger rather than rows the server can offset into.

    `useCallback` on personnelId alone keeps it stable: an unstable fetchPage re-runs the
    effect, calls setRows, re-renders and loops until React stops it.
  */
  const balances = useDataTable<LeaveCreditBalance>({
    pageSize: 25,
    fetchPage: useCallback(
      async (query, signal) => {
        const all = await leaveCreditApi.balances(personnelId, signal);
        const term = (query.search ?? '').trim().toLowerCase();
        const hits = all.filter((b) => b.leaveTypeName.toLowerCase().includes(term)
          || (b.leaveTypeCode ?? '').toLowerCase().includes(term));

        /*
          A CHILD IS NEVER SHOWN WITHOUT ITS PARENT. A matched child brings its parent row with
          it, so the indent still has something to sit under, and a sharing child's dashes still
          sit beside the balance they draw on. The server's parent-then-children order is kept.
        */
        const keep = new Set(hits.flatMap((b) => [b.leaveTypeId, b.parentLeaveTypeId ?? b.leaveTypeId]));
        const matched = term ? all.filter((b) => keep.has(b.leaveTypeId)) : all;

        const start = (query.page - 1) * query.pageSize;
        return { items: matched.slice(start, start + query.pageSize), total: matched.length };
      },
      [personnelId],
    ),
  });

  const ledger = useDataTable<LeaveLedgerEntry>({
    pageSize: 25,
    fetchPage: useCallback(
      async (query, signal) => {
        const [all, types] = await Promise.all([
          leaveCreditApi.ledger(personnelId, showDeleted, signal),
          leaveCreditApi.balances(personnelId, signal),
        ]);

        /*
          THE RAIL'S COUNTS are over the WHOLE ledger — never the searched or narrowed rows, or
          picking a type would zero every other entry. Types come in the balance table's
          parent-then-children order, so the rail and that table list them alike.
        */
        setLedgerAll(all.length);
        setRailTypes(types.map((t) => ({
          id: t.leaveTypeId,
          name: t.leaveTypeName,
          isChild: t.parentLeaveTypeId != null,
          count: all.filter((e) => e.leaveTypeId === t.leaveTypeId).length,
        })));

        const ofType = typeFilter === 'all' ? all : all.filter((e) => e.leaveTypeId === typeFilter);
        const term = (query.search ?? '').trim().toLowerCase();
        const matched = term
          ? ofType.filter((e) => e.leaveTypeName.toLowerCase().includes(term)
              || (e.reason ?? '').toLowerCase().includes(term))
          : ofType;

        /*
          LATEST FIRST at rest (product call, 27 Oct 2026); Date and Type sort both ways. The
          columns offered a sort this fetch ignored until then. Calendar dates compare as STRINGS
          (`YYYY-MM-DD` sorts in date order without the instant path), and the entry recorded
          last breaks a tie — latest first, whichever way the column points.
        */
        // useDataTable sends 'field:asc' or 'field:desc'. NO default sort: UNSORTED is latest first,
        // so the first click on Date turns it oldest-first rather than doing nothing visible.
        const [field = 'effectiveDate', order = 'desc'] = query.sort?.split(':') ?? [];
        const direction = order === 'asc' ? 1 : -1;
        const key = (e: LeaveLedgerEntry) => (field === 'leaveTypeName' ? e.leaveTypeName : e.effectiveDate);

        const sorted = [...matched].sort((a, b) =>
          direction * key(a).localeCompare(key(b))
          || b.effectiveDate.localeCompare(a.effectiveDate)
          || b.createdAt.localeCompare(a.createdAt));

        const start = (query.page - 1) * query.pageSize;
        return { items: sorted.slice(start, start + query.pageSize), total: sorted.length };
      },
      [personnelId, showDeleted, typeFilter],
    ),
  });

  /*
    THE LEAVE HISTORY — searched, sorted and paged IN MEMORY like the ledger beside it: the
    whole list arrives, so the filter sees everything there is. Most recent leave first.
  */
  const history = useDataTable<LeaveRecord>({
    pageSize: 25,
    defaultSort: '-startDate',
    fetchPage: useCallback(
      async (query, signal) => {
        const all = await leaveRecordApi.list(personnelId, showDeletedRecords, signal);
        const term = (query.search ?? '').trim().toLowerCase();
        const matched = term
          ? all.filter((r) =>
              [r.leaveTypeName, r.reason, r.approvalRemarks, r.filedByName, r.approvedByName]
                .some((value) => value != null && value.toLowerCase().includes(term)))
          : all;

        const field = query.sort?.replace(/^-/, '') ?? 'startDate';
        const direction = query.sort?.startsWith('-') ? -1 : 1;

        /*
          Calendar dates compared as STRINGS — `YYYY-MM-DD` sorts lexicographically in date
          order, and parsing one into a Date would put it through the instant path. An absent
          filing date sorts LAST whichever way the column points.
        */
        const pick = (r: LeaveRecord): string | number | null => {
          switch (field) {
            case 'leaveTypeName': return r.leaveTypeName;
            case 'endDate': return r.endDate;
            case 'days': return r.days;
            case 'filedDate': return r.filedDate ?? null;
            case 'approvedDate': return r.approvedDate;
            default: return r.startDate;
          }
        };

        const sorted = [...matched].sort((a, b) => {
          const av = pick(a);
          const bv = pick(b);
          if (av == null && bv == null) return 0;
          if (av == null) return 1;
          if (bv == null) return -1;
          const gap = typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av).localeCompare(String(bv));
          return gap * direction;
        });

        const start = (query.page - 1) * query.pageSize;
        return { items: sorted.slice(start, start + query.pageSize), total: sorted.length };
      },
      [personnelId, showDeletedRecords],
    ),
  });

  // A record that deducts moves the balance and writes the ledger, so all three reload together.
  const refresh = () => {
    balances.reload();
    ledger.reload();
    history.reload();
  };

  async function restoreRecord(row: LeaveRecord) {
    try {
      await leaveRecordApi.activate(personnelId, row.id);
      toast.success(`The ${row.leaveTypeName} record was restored.`);
      refresh();
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not restore that leave record.');
    }
  }

  /*
    THE COLUMNS ARE THE SCREEN'S OWN HEADINGS, in its order:
    BEGINNING · EARNED · USED · ADJUSTED · BALANCE · AWAITING APPROVAL.

    NOTHING SORTS. The figures are an aggregate the server does not order by, and the TYPE
    column's order IS the grouping — each parent followed by its children, indented (product
    call, 27 Oct 2026). A sort would scatter a child away from its parent, and a header
    promising one the list does not perform reads as broken.
  */
  const balanceColumns: DataColumn<LeaveCreditBalance>[] = [
    /*
      THE WIDTHS FIT THE PANEL, MEASURED RATHER THAN GUESSED.

      Sized when the panel was capped at the profile's 896px measure, where the seven columns
      had 763. At the catalogue's usual 120 they summed to 1078, which put BALANCE 85px past the
      edge and AWAITING APPROVAL entirely off-screen — the column the screen exists to show was
      the one the reader had to go looking for. The panel now takes the column's whole width
      (`fill`, 27 Oct 2026): 996px at a 1600px window, 926 after the `#` gutter, so these fit
      with room and the surplus is spread; they are kept as the floor for a narrow window.

      746 in all. The TYPE column takes 200 of it so "Special Privilege Leave" stays on
      ONE line: at 180 it wrapped, and a single two-line row in an otherwise uniform table
      reads as a rendering fault rather than a long name. The numeric columns gave up the
      width — they hold "0.00" under a nine-character heading and had slack to spare.

      Re-measure before widening any of them.
    */
    {
      key: 'leaveTypeName',
      header: 'Type',
      width: 200,
      render: (row) => (
        <Flagged row={row}>
          {row.parentLeaveTypeId != null ? (
            <span className="flex min-w-0 items-center gap-1.5 pl-3">
              <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-slate-300" aria-hidden="true" />
              <TextCell value={row.leaveTypeName} />
            </span>
          ) : (
            <TextCell value={row.leaveTypeName} />
          )}
        </Flagged>
      ),
    },
    {
      key: 'beginning',
      header: 'Beginning',
      width: 88,
      align: 'right',
      render: (row) => <Figure value={row.beginning} />,
    },
    {
      key: 'earned',
      header: 'Earned',
      width: 88,
      align: 'right',
      render: (row) => <Figure value={row.earned} signed />,
    },
    {
      key: 'used',
      header: 'Used',
      width: 85,
      align: 'right',
      render: (row) => <Figure value={row.used} debit />,
    },
    {
      key: 'adjusted',
      header: 'Adjusted',
      width: 88,
      align: 'right',
      render: (row) => <Figure value={row.adjusted} />,
    },
    {
      key: 'balance',
      header: 'Balance',
      width: 92,
      align: 'right',
      /*
        THE BALANCE OPENS ITS LEDGER (product call, 27 Oct 2026) — every row the figure is made
        of, oldest first with a running balance. A TABLE CELL's tone all the same (`slate-500`, regular —
        product call, 27 Oct 2026; root CLAUDE.md), told apart as clickable by its hover underline; red when
        below zero. A sharing child has no balance of its own and its em dash opens nothing.
      */
      render: (row) =>
        row.balance == null ? (
          <Figure value={null} />
        ) : (
          <Flagged row={row}>
            <button
              type="button"
              id={`leave-credit-balance-open-${row.leaveTypeId}`}
              onClick={() => setViewing(row)}
              aria-label={`${row.leaveTypeName} balance ${row.balance.toFixed(2)} — open its ledger`}
              className={`font-mono tabular-nums text-xs hover:underline ${
                row.balance < 0 ? 'text-red-600' : 'text-slate-500'
              }`}
            >
              {row.balance.toFixed(2)}
            </button>
          </Flagged>
        ),
    },
    {
      /*
        The heading TRUNCATES to `AWAITING APPROV…`, which is what the design shows and is
        correct: the alternative is taking width from Balance to spell out a column that reads
        an em dash on every row until leave requests exist.
      */
      key: 'awaitingApproval',
      header: 'Awaiting approval',
      width: 105,
      align: 'right',
      render: (row) => <Figure value={row.awaitingApproval} />,
    },
  ];

  /*
    THE WIDTHS FIT THE PANEL: 706px once the # and Actions gutters take 56 each, all of it.
    Date at 140 holds `Oct 31, 2026` on one line; Type and Reason give up width so Status
    stays clear of the pinned Actions gutter.

    They were once ignored, and that was not this table (resolved 27 Oct 2026): the three tabs
    share one slot of a ternary, so without a `key` React reused ONE panel and
    `useColumnWidths` — which reads its widths once, on mount — carried the Credit Balance
    table's figures into this one. Each table now carries its own `key`. Do not remove them.
  */
  const ledgerColumns: DataColumn<LeaveLedgerEntry>[] = [
    {
      key: 'effectiveDate',
      header: 'Date',
      width: 140,
      sortable: true,
      render: (row) => <TextCell value={formatDateOnly(row.effectiveDate)} mono />,
    },
    {
      key: 'leaveTypeName',
      header: 'Type',
      width: 150,
      sortable: true,
      render: (row) => <TextCell value={row.leaveTypeName} />,
    },
    {
      key: 'entryType',
      header: 'Entry',
      width: 90,
      render: (row) => <TextCell value={LEAVE_ENTRY_TYPE_LABELS[row.entryType]} />,
    },
    {
      key: 'days',
      header: 'Days',
      width: 76,
      align: 'right',
      /*
        SIGNED AS STORED, deliberately. The balance table renders Used positive because a
        column headed Used reading -3.00 negates twice — but a LEDGER prints what the row
        holds, and the sign is how a reader sees which way the entry moved the balance.
      */
      render: (row) => (
        <Figure value={row.days} signed={row.days > 0} />
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      width: 140,
      render: (row) => <TextCell value={row.reason} />,
    },
    {
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (row) => (
        <StatusText tone={row.status === LeaveEntryStatus.Approved ? 'active' : 'inactive'}>
          {LEAVE_ENTRY_STATUS_LABELS[row.status]}
        </StatusText>
      ),
    },
  ];

  /*
    LEAVE HISTORY — every leave taken, all of it approved, so no status beyond Active/Deleted.
    The TYPE is the anchor and opens the record as a preview; the period follows it.
  */
  const historyColumns: DataColumn<LeaveRecord>[] = [
    {
      key: 'leaveTypeName',
      header: 'Type',
      width: 200,
      sortable: true,
      render: (row) => <TextCell value={row.leaveTypeName} />,
    },
    {
      key: 'startDate',
      header: 'From',
      width: 140,
      sortable: true,
      summary: true,
      render: (row) => <TextCell value={formatDateOnly(row.startDate)} mono />,
    },
    {
      key: 'endDate',
      header: 'To',
      width: 140,
      sortable: true,
      summary: true,
      render: (row) => <TextCell value={formatDateOnly(row.endDate)} mono />,
    },
    {
      key: 'days',
      header: 'Days',
      width: 80,
      align: 'right',
      sortable: true,
      summary: true,
      render: (row) => <span className="font-mono tabular-nums text-xs text-slate-500">{row.days.toFixed(2)}</span>,
    },
    {
      key: 'deductsCredit',
      header: 'Leave credit',
      width: 150,
      render: (row) => <TextCell value={row.deductsCredit ? 'Deducted' : 'Recorded only'} />,
    },
    {
      // The type that covered what the leave type lacked; blank on a record that needed none.
      key: 'supplementaryLeaveTypeName',
      header: 'Supplementary type',
      width: 180,
      render: (row) => <TextCell value={row.supplementaryLeaveTypeName ?? null} />,
    },
    {
      key: 'supplementaryDays',
      header: 'Supplementary days',
      width: 110,
      align: 'right',
      render: (row) =>
        row.supplementaryDays != null ? (
          <span className="font-mono tabular-nums text-xs text-slate-500">{row.supplementaryDays.toFixed(2)}</span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      key: 'filedByName',
      header: 'Filed by',
      width: 160,
      render: (row) => <TextCell value={row.filedByName ?? null} />,
    },
    {
      key: 'filedDate',
      header: 'Filed at',
      width: 140,
      sortable: true,
      render: (row) => <TextCell value={row.filedDate != null ? formatDateOnly(row.filedDate) : null} mono />,
    },
    {
      key: 'reason',
      header: 'Reason',
      width: 220,
      render: (row) => <TextCell value={row.reason ?? null} />,
    },
    {
      key: 'approvedByName',
      header: 'Approved by',
      width: 160,
      render: (row) => <TextCell value={row.approvedByName ?? null} />,
    },
    {
      key: 'approvedDate',
      header: 'Approved at',
      width: 140,
      sortable: true,
      render: (row) => <TextCell value={formatDateOnly(row.approvedDate)} mono />,
    },
    {
      key: 'approvalRemarks',
      header: 'Approval remarks',
      width: 220,
      render: (row) => <TextCell value={row.approvalRemarks ?? null} />,
    },
    {
      // A panel that can show deleted rows owes a status column, or a deleted row is silent.
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (row) =>
        row.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/*
        No slate-100 ground: the strip stands on the profile itself and the table sits INSIDE
        the panel the active tab merges into, as Development Plan's and My Survey's do.
      */}
      <div className="flex min-h-0 flex-1 flex-col">
        <FolderTabs<Folder>
          id="leave-credits-folders"
          ariaLabel="Leaves"
          paddingX="px-3"
          activeKey={folder}
          onChange={setFolder}
          tabs={[
            { key: 'balance', label: 'Credit Balance' },
            {
              key: 'ledger',
              label: (
                <>
                  Leave Ledger <CountBadge count={ledgerAll} />
                </>
              ),
            },
            {
              key: 'history',
              label: (
                <>
                  Leave History <CountBadge count={history.total} />
                </>
              ),
            },
          ]}
        />

        <div className="flex min-h-0 flex-1 flex-col rounded-b-panel border border-slate-300 bg-white p-4">
          {folder === 'balance' ? (
            <DataTablePanel<LeaveCreditBalance>
              key="leave-credit-balances"
              id="leave-credit-balances"
              fill
              state={balances}
              columns={balanceColumns}
              rowKey={(row) => row.leaveTypeId}
              // A POSSIBLE discrepancy tints the row; the Type and Balance cells say why on hover.
              rowTone={(row) => ((row.discrepancies?.length ?? 0) > 0 ? 'red' : null)}
              searchPlaceholder="Search leave type…"
              /*
                A BALANCE ROW IS NOT A RECORD. It is one leave type's arithmetic over the ledger,
                so there is nothing to select, nothing to open and no provenance to show — the
                rows were never written. Create files a LEDGER entry, which is what moves these
                figures, so it carries the ledger's own key.
              */
              selectable={false}
              auditColumns={false}
              onCreate={() => setCreating(true)}
              createLabel="Record entry"
              createPermission="leave-credits.create"
              emptyMessage="No leave types are set up yet. Add them under Personnel → Settings → Leave Types."
              emptySearchMessage="No leave type matches that search."
            />
          ) : folder === 'ledger' ? (
            <div className="flex min-h-0 min-w-0 flex-1">
              <LeaveLedgerTypeRail
                id="leave-credit-ledger-types"
                active={typeFilter}
                total={ledgerAll}
                types={railTypes}
                onSelect={(key) => {
                  setTypeFilter(key);
                  // A narrower list re-reads from its first page.
                  ledger.setPage(1);
                }}
              />
              <div className="flex min-h-0 min-w-0 flex-1 flex-col pl-4">
                <DataTablePanel<LeaveLedgerEntry>
                  key="leave-credit-ledger"
                  id="leave-credit-ledger"
                  fill
                  state={ledger}
                  columns={ledgerColumns}
                  rowKey={(row) => row.id}
                  searchPlaceholder="Search the ledger…"
                  selectable={false}
                  /*
                    PROVENANCE AS COLUMNS (product call, 27 Oct 2026, reversing "popover only"): the
                    shell's Created and Latest update pair, never a hand-rolled created column. The
                    table now scrolls sideways inside the panel at its width — the accepted cost.

                    A DEDUCTION a Leave History record wrote is SYSTEM GENERATED. Its stored creator
                    is whoever filed the record, which would read as though they typed this row;
                    `otherInfo` names the system instead, so the column and the Other Info popover
                    say the same thing. The instant stays the real one. Its audit entry is filed
                    under that RECORD, so `Latest update` looks there.
                  */
                  otherInfo={(row) =>
                    row.leaveRecordId != null
                      ? { ...row, createdByName: SYSTEM_GENERATED, auditEntityId: row.leaveRecordId }
                      : row}
                  /*
                    Entries, not the onEdit/onDelete props, so a DEDUCTION a Leave History record
                    wrote can say why it is not edited here — the server refuses it too.
                  */
                  rowActions={(row) => {
                    const owned = row.leaveRecordId != null ? RECORD_OWNED_REASON : null;

                    return [
                      {
                        key: `edit-${row.id}`,
                        label: 'Edit',
                        permission: 'leave-credits.update',
                        disabledReason: owned,
                        onClick: () => setEditing(row),
                      },
                      {
                        key: `delete-${row.id}`,
                        label: 'Delete',
                        permission: 'leave-credits.delete',
                        destructive: true,
                        disabledReason: owned,
                        onClick: async () => {
                          await leaveCreditApi.deactivate(personnelId, row.id);
                          refresh();
                        },
                      },
                    ];
                  }}
                  emptyMessage="Nothing has been recorded against this employee yet."
                  emptySearchMessage="No entry matches that search."
                  settingsItems={[
                    {
                      key: 'show-deleted',
                      label: 'Show deleted',
                      checked: showDeleted,
                      onClick: () => setShowDeleted((v) => !v),
                      section: 'Records',
                    },
                  ]}
                />
              </div>
            </div>
          ) : (
            <DataTablePanel<LeaveRecord>
              key="leave-history"
              id="leave-history"
              fill
              state={history}
              columns={historyColumns}
              rowKey={(row) => row.id}
              searchPlaceholder="Search type, reason, remarks or account…"
              // The anchor opens the whole record — the remarks are prose a cell cannot hold.
              rowPreview={{ title: 'Leave record', size: '2xl' }}
              onCreate={() => setRecordCreating(true)}
              createLabel="Record leave"
              createPermission="leave-credits.create"
              /*
                Delete and Restore are the two halves of one state, so only the applicable one is
                offered; Edit on a deleted record is disabled with the reason — the server refuses it.
              */
              rowActions={(row) => [
                {
                  key: `edit-${row.id}`,
                  label: 'Edit',
                  permission: 'leave-credits.update',
                  disabledReason: row.isActive ? null : 'This leave record is deleted. Restore it before editing.',
                  onClick: () => setRecordEditing(row),
                },
                row.isActive
                  ? {
                      key: `delete-${row.id}`,
                      label: 'Delete',
                      permission: 'leave-credits.delete',
                      destructive: true,
                      onClick: () => setRecordDeleting([row]),
                    }
                  : {
                      key: `restore-${row.id}`,
                      label: 'Restore',
                      permission: 'leave-credits.delete',
                      onClick: () => void restoreRecord(row),
                    },
              ]}
              bulkActions={(selected) => {
                const deletable = selected.filter((r) => r.isActive);

                return [
                  {
                    key: 'delete-leave-records',
                    label: `Delete ${deletable.length} leave record${deletable.length === 1 ? '' : 's'}`,
                    permission: 'leave-credits.delete',
                    destructive: true,
                    disabledReason: deletable.length === 0 ? 'Every selected leave record is already deleted.' : null,
                    onClick: () => setRecordDeleting(deletable),
                  },
                ];
              }}
              otherInfo={(row) => ({
                createdAt: row.createdAt,
                createdByName: row.createdByName,
                updatedAt: row.updatedAt,
                updatedByName: row.updatedByName,
              })}
              emptyMessage="No leave has been recorded for this employee yet."
              emptySearchMessage="No leave record matches that search."
              settingsItems={[
                {
                  key: 'show-deleted',
                  label: 'Show deleted',
                  checked: showDeletedRecords,
                  onClick: () => setShowDeletedRecords((v) => !v),
                  section: 'Records',
                },
              ]}
            />
          )}
        </div>
      </div>

      {recordCreating || recordEditing ? (
        <LeaveRecordModal
          personnelId={personnelId}
          employeeUserId={employeeUserId}
          record={recordEditing}
          onClose={() => {
            setRecordCreating(false);
            setRecordEditing(null);
          }}
          onSaved={(result) => {
            toast.success(recordEditing ? 'The leave record was updated.' : 'The leave was recorded.');
            refresh();
            if (result?.keepOpen) return;
            setRecordCreating(false);
            setRecordEditing(null);
          }}
        />
      ) : null}

      {recordDeleting ? (
        <ConfirmDialog
          open
          id="leave-history-delete"
          title={
            recordDeleting.length === 1
              ? 'Delete this leave record?'
              : `Delete ${recordDeleting.length} leave records?`
          }
          danger
          requireConfirm
          body={
            <p className="text-sm text-slate-600">
              {recordDeleting.length === 1 ? 'The record is' : 'The records are'} hidden from the
              history and kept, and can be restored from Show deleted. Any days{' '}
              {recordDeleting.length === 1 ? 'it' : 'they'} deducted return to the leave credit
              balance until restored.
            </p>
          }
          onClose={() => setRecordDeleting(null)}
          onConfirm={async () => {
            /* One call per row — there is no bulk endpoint, and there should not be. */
            for (const row of recordDeleting) {
              await leaveRecordApi.deactivate(personnelId, row.id);
            }

            setRecordDeleting(null);
            refresh();
          }}
        />
      ) : null}

      {viewing ? (
        <LeaveTypeLedgerModal personnelId={personnelId} row={viewing} onClose={() => setViewing(null)} />
      ) : null}

      {creating || editing ? (
        <LeaveLedgerEntryModal
          personnelId={personnelId}
          entry={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}
